import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaServer, FaLaptopCode, FaShieldAlt, FaHeadset } from 'react-icons/fa';
import { Button, Card, CardBody } from "@heroui/react";

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Create scroll-based animations
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const y = useTransform(scrollY, [0, 300], [0, 100]);
  
  // Fade in elements on load
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  // Services for the hero slider
  const services = [
    {
      icon: <FaServer size={28} className="text-primary" />,
      title: "Infrastruktura IT",
      description: "Niezawodne rozwiązania serwerowe i sieciowe"
    },
    {
      icon: <FaShieldAlt size={28} className="text-primary" />,
      title: "Bezpieczeństwo IT",
      description: "Kompleksowa ochrona Twoich danych i systemów"
    },
    {
      icon: <FaLaptopCode size={28} className="text-primary" />,
      title: "Rozwój Oprogramowania",
      description: "Dedykowane aplikacje dla Twojego biznesu"
    },
    {
      icon: <FaHeadset size={28} className="text-primary" />,
      title: "Wsparcie Techniczne",
      description: "Zawsze gotowi, gdy nas potrzebujesz"
    }
  ];

  // Background elements
  const floatingIcons = [
    { icon: <FaServer size={24} />, left: '10%', top: '20%', delay: 0 },
    { icon: <FaLaptopCode size={28} />, left: '70%', top: '15%', delay: 1 },
    { icon: <FaShieldAlt size={20} />, left: '25%', top: '70%', delay: 0.5 },
    { icon: <FaServer size={18} />, left: '85%', top: '60%', delay: 1.5 },
    { icon: <FaLaptopCode size={22} />, left: '45%', top: '85%', delay: 2 }
  ];

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-gradient-to-b from-background via-background to-background/80">
      {/* Performance optimization - reduced number of animated elements */}
      {floatingIcons.slice(0, 3).map((item, index) => (
        <motion.div
          key={index}
          className="absolute text-primary opacity-10 hidden md:block"
          style={{ left: item.left, top: item.top }}
          animate={{
            y: [0, 15, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: 'reverse',
            delay: item.delay
          }}
        >
          {item.icon}
        </motion.div>
      ))}

      {/* Subtle grid background for visual texture */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAgIiBmaWxsPSJub25lIiBzdHJva2U9IiMxZjI5Mzc1MCIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIiAvPjwvc3ZnPg==')]" opacity={0.2} />
      
      {/* Gradient accents */}
      <div className="absolute left-0 top-1/4 w-64 h-64 bg-primary/5 rounded-full filter blur-xl" />
      <div className="absolute right-0 bottom-1/4 w-64 h-64 bg-secondary/5 rounded-full filter blur-xl" />

      {/* Main hero content */}
      <motion.div 
        style={{ opacity, y }}
        className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 md:pt-0"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column: Headline and CTA */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            className="text-center lg:text-left"
          >
            <motion.div variants={itemVariants}>
              <span className="inline-block text-primary font-semibold mb-2 tracking-wider">IT DLA KAŻDEGO</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                <span className="block mb-2">IT For Everyone</span>
                <span className="bg-gradient-to-r from-primary to-secondary text-transparent bg-clip-text">
                  Technologia dostępna dla wszystkich
                </span>
              </h1>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <p className="text-xl text-foreground-muted mb-8 max-w-xl mx-auto lg:mx-0">
                Dostarczamy przyjazne i zrozumiałe rozwiązania IT, które sprawią, 
                że technologia będzie służyć Twojemu biznesowi, a nie przeszkadzać w jego rozwoju.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center sm:items-start justify-center lg:justify-start gap-4 mb-12">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button 
                  as={Link} 
                  to="/kontakt" 
                  color="primary" 
                  size="lg"
                  endContent={<FaArrowRight />}
                  className="font-medium md:px-8"
                >
                  Konsultacja
                </Button>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button 
                  as={Link} 
                  to="/uslugi" 
                  variant="bordered" 
                  color="primary" 
                  size="lg"
                  className="font-medium"
                >
                  Poznaj nasze usługi
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right column: Services preview */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-primary/10 rounded-full blur-lg" />
              <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-secondary/10 rounded-full blur-lg" />
              
              {/* Service cards */}
              <div className="grid grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + (index * 0.1) }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  >
                    <Card className="shadow-md backdrop-blur-sm bg-background/80 border border-divider/10">
                      <CardBody>
                        <div className="flex items-start">
                          <div className="mr-4 mt-1">{service.icon}</div>
                          <div>
                            <h3 className="font-semibold mb-1">{service.title}</h3>
                            <p className="text-sm text-foreground-muted">{service.description}</p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Trust indicators - zmniejszone do mniejszej firmy */}
        <motion.div 
          variants={itemVariants} 
          className="mt-12 mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <Card className="max-w-4xl mx-auto shadow-lg border border-primary/10" isBlurred>
            <CardBody className="py-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <h3 className="text-xl font-semibold text-center md:text-left">
                  Indywidualne podejście i <span className="text-primary">spersonalizowane rozwiązania</span>
                </h3>
                
                <Button 
                  as={Link} 
                  to="/o-nas" 
                  color="primary" 
                  variant="flat"
                  size="sm"
                >
                  Poznaj nas bliżej
                </Button>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </motion.div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2 hidden md:block"
        animate={{ y: [0, 8, 0], opacity: 1 }}
        transition={{ repeat: Infinity, duration: 2, delay: 2 }}
        initial={{ opacity: 0 }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-primary/50 flex items-center justify-center">
          <motion.div 
            className="w-1.5 h-1.5 bg-primary rounded-full"
            animate={{ y: [0, 4, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default Hero;